// The left pane of Messages: the heading (with the page's New button beside it), a
// find box, and ONE list of the person's one-to-one chats and groups, newest first.
// Each row is a link to its conversation, so the address changes with what is open and
// Back steps through them.
//
// It draws what the page hands it and asks the server nothing itself.
import { useMemo, useState } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Users } from 'lucide-react';
import { SearchInput } from '../SearchInput';
import { conversationPath, type OpenConversation } from './messagesPath';
import { initials, rowLine, rowTime, unreadLabel, type ListRow } from './messageText';

interface ConversationListProps {
  rows: ListRow[];
  /** The person's own employee code, lower case: whose messages read "You: …" in a group. */
  me: string;
  open: OpenConversation | null;
  /** More rows on the server than are loaded. */
  hasMore: boolean;
  loadingMore: boolean;
  onLoadMore: () => void;
  /** One side of the list did not load while the other did. Said in one line, with Try again. */
  partFailed: boolean;
  onRetry: () => void;
  /** Drawn at the right of the heading: the page's New button, when it has one. */
  action?: ReactNode;
}

function Row({ item, me, isOpen }: { item: ListRow; me: string; isOpen: boolean }): ReactElement {
  const line = rowLine(item, me);
  const time = rowTime(item.row.lastMessageAt);
  const unread = item.row.unreadCount;
  return (
    <li>
      <Link
        to={conversationPath({ kind: item.kind, id: item.id })}
        aria-current={isOpen ? 'true' : undefined}
        className={`flex items-center gap-3 rounded-xl px-3 py-2.5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${
          isOpen ? 'bg-primary-50' : 'hover:bg-gray-50'
        }`}
      >
        <span
          aria-hidden="true"
          className="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-primary-50 text-sm font-semibold text-primary-700"
        >
          {item.kind === 'group' ? <Users className="h-5 w-5" /> : initials(item.name)}
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex items-baseline gap-2">
            <span className={`min-w-0 flex-1 truncate text-sm text-gray-900 ${unread > 0 ? 'font-semibold' : 'font-medium'}`}>
              {item.name}
            </span>
            {time && <span className="flex-none text-xs text-gray-400">{time}</span>}
          </span>
          <span className="mt-0.5 flex items-center gap-2">
            <span className={`min-w-0 flex-1 truncate text-sm ${unread > 0 ? 'text-gray-700' : 'text-gray-500'}`} title={line || undefined}>
              {line}
            </span>
            {unread > 0 && (
              <span
                aria-label={`${unread} unread`}
                className="flex h-5 min-w-[20px] flex-none items-center justify-center rounded-full bg-primary-600 px-1.5 text-[11px] font-bold leading-none text-white"
              >
                {unreadLabel(unread)}
              </span>
            )}
          </span>
        </span>
      </Link>
    </li>
  );
}

export default function ConversationList({
  rows, me, open, hasMore, loadingMore, onLoadMore, partFailed, onRetry, action,
}: ConversationListProps): ReactElement {
  const [find, setFind] = useState('');
  // The find box narrows the rows that are loaded, by name. It asks the server nothing.
  const shown = useMemo(() => {
    const wanted = find.trim().toLowerCase();
    return wanted ? rows.filter((r) => r.name.toLowerCase().includes(wanted)) : rows;
  }, [rows, find]);

  return (
    <>
      <div className="flex-none border-b border-gray-100 px-4 pb-3 pt-4">
        <div className="flex items-center justify-between gap-3">
          <h1 className="text-xl font-bold tracking-tight text-gray-900">Messages</h1>
          {action}
        </div>
        <div className="mt-3" role="search" aria-label="Find a conversation">
          <SearchInput value={find} onChange={setFind} placeholder="Find by name" size="md" />
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto p-2">
        {shown.length === 0 ? (
          <p className="px-3 py-6 text-center text-sm text-gray-500">
            {rows.length === 0 ? 'No messages yet.' : 'No conversation by that name.'}
          </p>
        ) : (
          <ul aria-label="Conversations" className="space-y-0.5">
            {shown.map((item) => (
              <Row key={item.key} item={item} me={me} isOpen={open !== null && open.kind === item.kind && open.id === item.id} />
            ))}
          </ul>
        )}
        {partFailed && (
          <p className="px-3 py-3 text-center text-sm text-gray-500">
            Some conversations could not be loaded.{' '}
            <button
              type="button"
              onClick={onRetry}
              className="font-semibold text-primary-600 hover:text-primary-700 focus:outline-none focus-visible:underline"
            >
              Try again
            </button>
          </p>
        )}
        {hasMore && (
          <div className="flex justify-center px-3 py-3">
            <button
              type="button"
              onClick={onLoadMore}
              disabled={loadingMore}
              className="rounded-md border border-gray-300 px-4 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              {loadingMore ? 'Loading…' : 'Load more'}
            </button>
          </div>
        )}
      </div>
    </>
  );
}
